import type { ApiResponse, ApiError, RequestConfig } from './types';

export interface ApiClientConfig {
  baseUrl: string;
  getAccessToken: () => string | null;
  onUnauthorized?: () => void;
}

export interface ApiClient {
  get<T>(path: string, config?: RequestConfig): Promise<ApiResponse<T>>;
  post<T>(path: string, data?: unknown, config?: RequestConfig): Promise<ApiResponse<T>>;
  put<T>(path: string, data?: unknown, config?: RequestConfig): Promise<ApiResponse<T>>;
  patch<T>(path: string, data?: unknown, config?: RequestConfig): Promise<ApiResponse<T>>;
  delete<T>(path: string, config?: RequestConfig): Promise<ApiResponse<T>>;
}

export function createApiClient(config: ApiClientConfig): ApiClient {
  const { baseUrl, getAccessToken, onUnauthorized } = config;

  async function request<T>(
    method: string,
    path: string,
    data?: unknown,
    requestConfig?: RequestConfig
  ): Promise<ApiResponse<T>> {
    const url = `${baseUrl}${path}`;
    const token = getAccessToken();

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...requestConfig?.headers,
    };

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    try {
      const response = await fetch(url, {
        method,
        headers,
        body: data ? JSON.stringify(data) : undefined,
        signal: requestConfig?.signal,
      });

      if (response.status === 401) {
        onUnauthorized?.();
        throw createApiError('Unauthorized', 401);
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw createApiError(
          errorData.error || errorData.message || `Request failed with status ${response.status}`,
          response.status,
          errorData
        );
      }

      // Handle empty responses (204 No Content or empty body)
      const contentLength = response.headers.get('content-length');
      if (response.status === 204 || contentLength === '0') {
        return { data: undefined as T, status: response.status };
      }

      const responseData = await response.json();
      return { data: responseData, status: response.status };
    } catch (error) {
      if (error instanceof Error && 'status' in error) {
        throw error;
      }
      throw createApiError(error instanceof Error ? error.message : 'Network error', 0);
    }
  }

  return {
    get: <T>(path: string, config?: RequestConfig) => request<T>('GET', path, undefined, config),
    post: <T>(path: string, data?: unknown, config?: RequestConfig) =>
      request<T>('POST', path, data, config),
    put: <T>(path: string, data?: unknown, config?: RequestConfig) =>
      request<T>('PUT', path, data, config),
    patch: <T>(path: string, data?: unknown, config?: RequestConfig) =>
      request<T>('PATCH', path, data, config),
    delete: <T>(path: string, config?: RequestConfig) =>
      request<T>('DELETE', path, undefined, config),
  };
}

function createApiError(message: string, status: number, details?: unknown): ApiError {
  const error = new Error(message) as ApiError;
  error.status = status;
  error.details = details;
  return error;
}
